<av-content-container [fullHeight]="true">
  <div class="av-all-demand-detail d-flex flex-column py-3 px-3 px-md-4">
    @if (invalidRoute() || facade.notFound()) {
      <div class="av-all-demand-detail__notfound d-flex flex-column align-items-center gap-3 py-5">
        <mat-icon class="text-muted">error_outline</mat-icon>
        <span>{{ 'app.reports.allDemands.detail.notFound' | translate }}</span>
        <a mat-stroked-button [routerLink]="listRoute">
          <mat-icon>arrow_back</mat-icon>
          {{ 'app.reports.allDemands.detail.back' | translate }}
        </a>
      </div>
    } @else if (facade.loadFailed()) {
      <av-empty-state icon="error" titleKey="shared.api.errors.default">
        <button
          type="button"
          mat-stroked-button
          [disabled]="facade.loading()"
          [avPending]="retryWait.visible()"
          (click)="facade.reload()"
        >
          @if (retryWait.visible()) {
            <av-spin size="sm" />
          } @else {
            <mat-icon>refresh</mat-icon>
          }
          <span>{{ 'shared.general.tooltips.refresh' | translate }}</span>
        </button>
      </av-empty-state>
    } @else if (coldWait.visible()) {
      <div class="av-all-demand-detail__skeleton d-flex flex-column">
        <div class="av-all-demand-detail__skeleton-back d-flex align-items-center mb-3">
          <av-skeleton shape="count" />
        </div>

        <section class="av-hero p-3 p-md-4 mb-3">
          <div class="d-flex flex-column flex-md-row align-items-md-center gap-3">
            <span class="av-all-demand-detail__skeleton-icon"></span>
            <div class="d-flex flex-column flex-grow-1 gap-2">
              <av-skeleton class="av-all-demand-detail__skeleton-title" shape="text" [rows]="1" />
              <av-skeleton class="av-all-demand-detail__skeleton-kind" shape="text" [rows]="1" />
              <av-skeleton shape="pills" [pills]="[92, 176]" />
            </div>
          </div>
        </section>

        <div class="row g-4">
          <div class="col-12 col-lg-7">
            <div class="av-panel p-3 p-md-4 h-100">
              <av-skeleton class="mb-3" shape="count" />
              <div class="d-flex flex-column">
                @for (step of skeletonStepRows; track step) {
                  <div class="av-all-demand-detail__skeleton-step">
                    <span class="av-all-demand-detail__skeleton-badge"></span>
                    <div class="d-flex flex-column gap-2 pb-3">
                      <av-skeleton shape="text" [rows]="1" />
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-5">
            <div class="av-panel p-3 p-md-4 h-100">
              <av-skeleton class="mb-3" shape="count" />
              <div class="d-flex flex-column">
                @for (row of skeletonActivityRows; track row) {
                  <div class="av-all-demand-detail__skeleton-activity">
                    <span class="av-all-demand-detail__skeleton-marker"></span>
                    <div class="d-flex flex-column gap-2 pb-3">
                      <av-skeleton shape="text" [rows]="1" />
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else if (facade.demand(); as demand) {
      <av-rail class="position-absolute top-0 start-0 w-100" [active]="refetchWait.visible()" />

      <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
        <a
          [routerLink]="listRoute"
          class="av-all-demand-detail__back d-inline-flex align-items-center gap-1 small text-muted"
        >
          <mat-icon>arrow_back</mat-icon>
          {{ 'app.reports.allDemands.detail.back' | translate }}
        </a>

        <button
          mat-icon-button
          type="button"
          class="flex-shrink-0"
          [disabled]="facade.loading()"
          [avPending]="refetchWait.visible()"
          [attr.aria-label]="'shared.general.tooltips.refresh' | translate"
          [matTooltip]="'shared.general.tooltips.refresh' | translate"
          (click)="facade.reload()"
        >
          @if (refetchWait.visible()) {
            <av-spin size="sm" />
          } @else {
            <mat-icon>refresh</mat-icon>
          }
        </button>
      </div>

      <section class="av-hero p-3 p-md-4 mb-3">
        <div class="d-flex flex-column flex-md-row align-items-md-center gap-3">
          <div
            class="av-hero__icon d-flex align-items-center justify-content-center"
            [style.background]="gradient()"
          >
            @if (isApplication() && iconUrl()) {
              <img avAppIcon class="av-hero__icon-img" [src]="iconUrl()" [alt]="subjectName()" />
            } @else if (isApplication()) {
              <span class="av-hero__icon-img av-app-icon-placeholder" aria-hidden="true"></span>
            } @else {
              <mat-icon class="av-hero__icon-placeholder" [style.color]="groupIconColor()">{{
                groupIcon()
              }}</mat-icon>
            }
          </div>

          <div class="d-flex flex-column flex-grow-1 av-hero__main">
            <h1 class="av-hero__title h3 m-0 d-inline-flex align-items-center gap-2">
              <span class="text-truncate">{{ subjectName() }}</span>
              @if (subjectDeleted()) {
                <span class="av-deleted-badge">{{
                  'shared.general.labels.deleted' | translate
                }}</span>
              }
            </h1>
            <span class="small text-muted">{{ kindKey() | translate }}</span>

            <div class="d-flex align-items-center gap-2 mt-2 flex-wrap">
              @if (status(); as currentStatus) {
                <app-demand-status-chip [status]="currentStatus"></app-demand-status-chip>
              }
              <span
                class="text-muted small"
                [matTooltip]="demand.created_at | date: 'dd/MM/yyyy HH:mm'"
              >
                {{ 'app.reports.allDemands.detail.requestedOn' | translate }}
                {{ demand.created_at | date: 'dd/MM/yyyy' }}
              </span>
              <span class="d-inline-flex align-items-center gap-1 small">
                <span class="text-muted">{{
                  'app.reports.allDemands.detail.reference' | translate
                }}</span>
                <av-reference [reference]="demand.reference" />
              </span>
            </div>
          </div>
        </div>

        <dl class="av-hero__facts m-0 mt-3">
          <div class="av-hero__fact">
            <dt>{{ 'app.reports.allDemands.detail.requestedBy' | translate }}</dt>
            <dd class="d-inline-flex align-items-center gap-2 m-0">
              @if (requesterName()) {
                <av-avatar [name]="requesterName()" size="sm"></av-avatar>
                <span class="text-truncate">{{ requesterName() }}</span>
              } @else {
                <span class="text-muted">{{ 'shared.general.labels.na' | translate }}</span>
              }
            </dd>
          </div>
          <div class="av-hero__fact">
            <dt>{{ 'app.reports.allDemands.detail.moderator' | translate }}</dt>
            <dd class="m-0">
              @if (moderatorName()) {
                {{ moderatorName() }}
              } @else {
                <span class="text-muted">{{
                  'app.reports.allDemands.detail.noModerator' | translate
                }}</span>
              }
            </dd>
          </div>
          @if (deviceLabel()) {
            <div class="av-hero__fact">
              <dt>{{ 'app.reports.allDemands.detail.device' | translate }}</dt>
              <dd class="m-0">{{ deviceLabel() }}</dd>
            </div>
          }
          @if (demand.comment) {
            <div class="av-hero__fact av-hero__fact--wide">
              <dt>{{ 'app.reports.allDemands.detail.comment' | translate }}</dt>
              <dd class="m-0">{{ demand.comment }}</dd>
            </div>
          }
        </dl>
      </section>

      <div class="av-banner d-flex align-items-center gap-2 mb-3">
        <mat-icon class="av-banner__icon">visibility</mat-icon>
        <span>{{ 'app.reports.allDemands.detail.readOnly' | translate }}</span>
      </div>

      <div class="row g-4">
        <div class="col-12 col-lg-7">
          <div class="av-panel p-3 p-md-4 h-100">
            <h2 class="h6 mb-1">{{ 'app.reports.allDemands.detail.chain.title' | translate }}</h2>
            <p class="small text-muted mb-3">
              {{ 'app.reports.allDemands.detail.chain.subtitle' | translate }}
            </p>
            @if (hasChain()) {
              <app-approval-steps-stepper [steps]="demand.approvals" />
            } @else {
              <span class="text-muted">{{
                'app.reports.allDemands.detail.chain.none' | translate
              }}</span>
            }
          </div>
        </div>

        <div class="col-12 col-lg-5">
          <div class="av-panel p-3 p-md-4 h-100">
            <h2 class="h6 mb-3">
              {{ 'app.reports.allDemands.detail.activity.title' | translate }}
            </h2>
            <app-demand-activity-log [demand]="demand"></app-demand-activity-log>
          </div>
        </div>
      </div>
    }
  </div>
</av-content-container>
